import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { saveFee } from './feesApi';

function receiptNo() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const rand = String(Math.floor(Math.random() * 900) + 100);
  return `RCP-${y}${m}${day}-${rand}`;
}

/** Create a fee receipt covering one or more students. */
export async function createFeeReceipt({ lines, paymentMethod, notes, createdByUid, createdByName }) {
  if (!db) throw new Error('Firestore not configured.');
  if (!Array.isArray(lines) || lines.length === 0) {
    throw new Error('Select at least one student.');
  }

  const validLines = lines
    .map((l) => ({
      studentId: String(l.studentId || '').trim(),
      studentName: String(l.studentName || '').trim(),
      amount: Number(l.amount) || 0,
      monthsPaid: String(l.monthsPaid || '')
        .split(/[\n,]+/)
        .map((m) => m.trim())
        .filter(Boolean),
      description: String(l.description || 'Fee payment').trim(),
    }))
    .filter((l) => l.studentId && l.studentName);

  if (validLines.length === 0) throw new Error('No valid student lines.');

  const totalAmount = validLines.reduce((sum, l) => sum + l.amount, 0);
  const id = doc(collection(db, 'feeReceipts')).id;
  const now = new Date().toISOString();

  await setDoc(doc(db, 'feeReceipts', id), {
    receiptNo: receiptNo(),
    paidAt: now,
    date: now.slice(0, 10),
    paymentMethod: String(paymentMethod || 'cash').trim(),
    totalAmount,
    lines: validLines,
    notes: String(notes || '').trim(),
    createdByUid: createdByUid || null,
    createdByName: createdByName || null,
    createdAt: serverTimestamp(),
  });

  for (const line of validLines) {
    const feeSnap = await getDoc(doc(db, 'fees', line.studentId));
    const prev = feeSnap.exists() ? feeSnap.data() : {};
    const prevMonths = Array.isArray(prev.monthsPaid) ? prev.monthsPaid : [];
    const mergedMonths = [...new Set([...prevMonths, ...line.monthsPaid])];
    const prevDue = Number(prev.amountDue) || 0;
    const newDue = Math.max(0, prevDue - line.amount);
    await saveFee({
      studentId: line.studentId,
      studentName: line.studentName,
      parentUid: prev.parentUid || '',
      amountDue: newDue,
      monthsPaid: mergedMonths.join(', '),
    });
  }

  return { id, totalAmount, lineCount: validLines.length };
}

export async function listFeeReceipts() {
  if (!db) return [];
  const snap = await getDocs(collection(db, 'feeReceipts'));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(b.paidAt || '').localeCompare(String(a.paidAt || '')));
}

export async function deleteFeeReceipt(receiptId) {
  if (!db) throw new Error('Firestore not configured.');
  const id = String(receiptId || '').trim();
  if (!id) throw new Error('Receipt ID is required.');
  await deleteDoc(doc(db, 'feeReceipts', id));
}
